import _ from 'underscore';

import {Sequence as ComponentSequence} from 'components/Sequence';
import * as sequtil from 'molbio/SeqUtil';

interface LayeredModel extends Backbone.Model {
  layerStart(sequence?: ComponentSequence | null): number;
  layerEnd(sequence?: ComponentSequence | null): number;
  start(): number;
  end(): number;
}

export interface Layer<Model extends {cid: string}> {
  /** A model to be rendered on a linear map, minimap, or circular map */
  model: Model;
  /**
   * Which layer the model should be rendered in. This is a natural number, increasing down for linear maps
   * and inward for circular maps.
   */
  layer: number;
}

export interface LayersData<Model extends {cid: string}> {
  /** The total number of layers needed to render all the models */
  max: number;
  /** All the models and what layer they should be rendered on */
  layers: Array<Layer<Model>>;
  /** Map of model cid to the model that follows it in the same layer */
  siblingMap: {[cid: string]: Model};
}

interface GetLayersOptions<Model extends LayeredModel> {
  startFn: (model: Model) => {model: Model; pos: number};
  endFn: (model: Model, options?: {layer: number}) => {model: Model; pos: number};
  top: boolean;
  sequence: null | ComponentSequence;
}

function getModelLayers<Model extends LayeredModel>(
  models: Iterable<Model>,
  options?: Partial<GetLayersOptions<Model>>
): Array<Layer<Model>> {
  return getLayers(models, options).layers;
}

function getMax<Model extends LayeredModel>(
  models: Iterable<Model>,
  options?: Partial<GetLayersOptions<Model>>
): number {
  return getLayers(models, options).max;
}

function getLayers<Model extends LayeredModel>(
  models: Iterable<Model>,
  options: Partial<GetLayersOptions<Model>> = {}
): LayersData<Model> {
  const optionsWithDefaults: GetLayersOptions<Model> = {
    startFn(model: Model): {model: Model; pos: number} {
      return {model, pos: model.layerStart(options.sequence)};
    },
    endFn(model: Model): {model: Model; pos: number} {
      return {model, pos: model.layerEnd(options.sequence)};
    },
    top: false,
    sequence: null,
    ...options,
  };
  const {startFn, endFn, top, sequence} = optionsWithDefaults;

  let sortedModels = [...models];
  if (sequence != null) {
    const seqLength = sequence.length();
    sortedModels = _.sortBy(sortedModels, (model) => -sequtil.length(model.start(), model.end(), seqLength));
  }

  return getFeatureLayers(sortedModels, startFn, endFn, top);
}

/**
 * Fits features into layers for rendering based on their start and end positions such that
 * no features on a single layer overlap with each other.
 * Attempts to fit cross-origin features into layers with other features, but if not immediately possible,
 * each extra feature is put on its own layer at the end.
 */
function getFeatureLayers<T extends {cid: string}>(
  features: Array<T>,
  startFn: (model: T) => {model: T; pos: number},
  endFn: (model: T, options?: {layer: number}) => {model: T; pos: number},
  top: boolean
): LayersData<T> {
  const firstUsed: {[index: number]: T} = {}; // Maps each layer to the first feature in that layer
  const lastUsed: {[index: number]: T} = {}; // Maps each layer to the last feature in that layer
  const used: Array<T | null> = [];
  const layers: Array<{model: T; layer: number}> = [];
  const extraWrapFeatures: Array<T> = []; // Any cross-origin features that don't fit on existing layers

  const siblingMap: {[cid: string]: T} = {}; // Maps cid of feature to the feature that follows it.

  const ends: Array<{model: T; pos: number}> = [];
  const starts = _.sortBy(
    features.map((model) => startFn(model)),
    'pos'
  );

  // Add features to layers in order by the feature start position.
  while (starts.length) {
    if (starts.length && (!ends.length || starts[0].pos < ends[0].pos || ends[0].pos === 0)) {
      // This feature will overlap with the used portion of all layers that have not yet been checked,
      // since the remaining unchecked layer ends all end after than this feature starts.
      // Add it to an existing layer that we know to be free or start a new layer.
      let newLayer: boolean;
      // Safe because array length is non-zero.
      const {model: feature, pos: featureStart} = starts.shift()!;

      // Get the first layer that this feature may fit in, and mark it as used.
      let layerIndex = used.indexOf(null);
      if (layerIndex === -1) {
        layerIndex = used.length;
        used.push(feature);
        newLayer = true;
      } else {
        used[layerIndex] = feature;
        newLayer = false;
      }

      // Keep track of the first thing used on each layer.
      const oldFirstUsed = firstUsed[layerIndex];
      const oldLastUsed = lastUsed[layerIndex];
      if (oldFirstUsed == null) {
        firstUsed[layerIndex] = feature;
      }
      lastUsed[layerIndex] = feature;

      const end = endFn(feature, {layer: layerIndex});

      if (featureStart < end.pos) {
        // If the feature is not cross-origin, add it to this layer.
        layers.push({model: feature, layer: layerIndex});
        siblingMap[feature.cid] = oldFirstUsed ?? feature;
        if (oldLastUsed != null) {
          siblingMap[oldLastUsed.cid] = feature;
        }

        // Insert end, since this layer may still have room for later features.
        const endIndex = _.sortedIndex(ends, end, (end) => {
          if (end.pos === 0) {
            return Infinity;
          } else {
            return end.pos;
          }
        });
        ends.splice(endIndex, 0, end);
      } else if (end.pos === 0 || (oldFirstUsed != null && end.pos <= startFn(oldFirstUsed).pos)) {
        // If the feature ends at 0 or is cross-origin but fits on this layer, this layer is completed
        // and cannot fit any more features because all later features have a greater start than this one,
        // so we do not add it to ends.
        layers.push({model: feature, layer: layerIndex});
        siblingMap[feature.cid] = oldFirstUsed || feature;
        if (oldLastUsed != null) {
          siblingMap[oldLastUsed.cid] = feature;
        }
      } else {
        // The feature is cross-origin and does not fit on the existing layer,
        // so we'll add it to its own layer at the end.
        extraWrapFeatures.push(feature);
        // Undo earlier changes to existing layer
        if (newLayer) {
          used.pop();
        } else {
          used[layerIndex] = null;
        }
        firstUsed[layerIndex] = oldFirstUsed;
        lastUsed[layerIndex] = oldLastUsed;
        siblingMap[feature.cid] = feature;
      }
    } else {
      // We know the end of the used portion of the first layer in ends will not overlap with the start
      // of any subsequent features to add, so mark the layer as unused and remove it from ends
      // since we do not need to check it again.
      const end = ends.shift()!;
      const index = used.indexOf(end.model);
      used[index] = null;
    }
  }

  let max = used.length - 1;

  // Add leftover cross-origin features that aren't already included to their own individual layers.
  for (const feature of extraWrapFeatures) {
    layers.push({
      model: feature,
      layer: ++max,
    });
  }

  if (!top) {
    for (const el of layers) {
      el.layer = max - el.layer;
    }
  }

  return {
    max,
    layers,
    siblingMap,
  };
}

export {getLayers, getModelLayers, getMax, getFeatureLayers};
